import { useState, useEffect } from "react";
import Modal from "./common/Modal";
import styles from "../assets/styles/Modal.module.css";

const STAGES = [
   { id: "sync_events", label: "Синхронизация событий", description: "Сбор событий из базы данных" },
   { id: "collect_identifiers", label: "Сбор идентификаторов", description: "Сбор IP, кошельков, device fingerprint" },
   { id: "detect_links", label: "Обнаружение связей", description: "Поиск общих кошельков, IP и других связей" },
   { id: "calculate_risks", label: "Расчет рисков", description: "Вычисление risk_score для пользователей" },
];

export default function RecalculationProgressModal({ 
   open, 
   onClose, 
   isAllUsers = false,
   userName = null,
   canClose = false
}) {
   const [currentStage, setCurrentStage] = useState(0);
   const [completedStages, setCompletedStages] = useState([]);

   useEffect(() => {
      if (!open) {
         setCurrentStage(0);
         setCompletedStages([]);
         return;
      }

      // Анимация этапов
      const stageIntervals = STAGES.map((stage, index) => {
         return setTimeout(() => {
            setCurrentStage(index);
            // Через некоторое время помечаем этап как завершенный
            setTimeout(() => {
               setCompletedStages(prev => [...prev, stage.id]);
            }, 1500);
         }, index * 2000);
      });

      return () => {
         stageIntervals.forEach(clearTimeout);
      };
   }, [open]);

   const handleClose = () => {
      if (canClose) {
         onClose();
      }
   };

   const progress = ((completedStages.length / STAGES.length) * 100).toFixed(0);

   return (
      <Modal 
         open={open} 
         onClose={canClose ? handleClose : undefined}
         title={isAllUsers ? "Пересчет рисков для всех пользователей" : `Пересчет риска для ${userName || "пользователя"}`}
      >
         <div style={{ padding: "20px" }}>
            <div style={{ 
               marginBottom: "30px",
               textAlign: "center"
            }}>
               <div style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginBottom: "10px"
               }}>
                  <div style={{
                     width: `${progress}%`,
                     height: "100%",
                     backgroundColor: "#007bff",
                     transition: "width 0.5s ease",
                     borderRadius: "4px"
                  }} />
               </div>
               <div style={{ 
                  fontSize: "14px", 
                  color: "#9ca3af",
                  marginTop: "8px"
               }}>
                  {progress}% завершено
               </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
               {STAGES.map((stage, index) => {
                  const isActive = currentStage === index;
                  const isCompleted = completedStages.includes(stage.id);
                  const isPending = index > currentStage;

                  return (
                     <div
                        key={stage.id}
                        style={{
                           display: "flex",
                           alignItems: "center",
                           gap: "16px",
                           padding: "12px",
                           borderRadius: "8px",
                           backgroundColor: isActive 
                              ? "rgba(0, 123, 255, 0.1)" 
                              : isCompleted
                              ? "rgba(40, 167, 69, 0.1)"
                              : "rgba(255, 255, 255, 0.03)",
                           border: `1px solid ${
                              isActive 
                                 ? "rgba(0, 123, 255, 0.3)" 
                                 : isCompleted
                                 ? "rgba(40, 167, 69, 0.3)"
                                 : "rgba(255, 255, 255, 0.05)"
                           }`,
                           transition: "all 0.3s ease"
                        }}
                     >
                        <div style={{
                           width: "24px",
                           height: "24px",
                           borderRadius: "50%",
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           backgroundColor: isCompleted
                              ? "#28a745"
                              : isActive
                              ? "#007bff"
                              : "rgba(255, 255, 255, 0.1)",
                           color: "#fff",
                           fontSize: "12px",
                           fontWeight: "bold",
                           flexShrink: 0
                        }}>
                           {isCompleted ? (
                              "✓"
                           ) : isActive ? (
                              <div style={{
                                 width: "8px",
                                 height: "8px",
                                 borderRadius: "50%",
                                 backgroundColor: "#fff",
                                 animation: "pulse 1.5s infinite"
                              }} />
                           ) : (
                              index + 1
                           )}
                        </div>
                        <div style={{ flex: 1 }}>
                           <div style={{
                              fontSize: "15px",
                              fontWeight: isActive ? "600" : "400",
                              color: isActive ? "#fff" : isCompleted ? "#28a745" : "#9ca3af",
                              marginBottom: "4px"
                           }}>
                              {stage.label}
                           </div>
                           <div style={{
                              fontSize: "12px",
                              color: "#6b7280"
                           }}>
                              {stage.description}
                           </div>
                        </div>
                        {isActive && (
                           <div style={{
                              display: "flex",
                              gap: "4px"
                           }}>
                              <div style={{
                                 width: "6px",
                                 height: "6px",
                                 borderRadius: "50%",
                                 backgroundColor: "#007bff",
                                 animation: "bounce 1.4s infinite",
                                 animationDelay: "0s"
                              }} />
                              <div style={{
                                 width: "6px",
                                 height: "6px",
                                 borderRadius: "50%",
                                 backgroundColor: "#007bff",
                                 animation: "bounce 1.4s infinite",
                                 animationDelay: "0.2s"
                              }} />
                              <div style={{
                                 width: "6px",
                                 height: "6px",
                                 borderRadius: "50%",
                                 backgroundColor: "#007bff",
                                 animation: "bounce 1.4s infinite",
                                 animationDelay: "0.4s"
                              }} />
                           </div>
                        )}
                     </div>
                  );
               })}
            </div>

            <style>{`
               @keyframes pulse {
                  0%, 100% {
                     opacity: 1;
                     transform: scale(1);
                  }
                  50% {
                     opacity: 0.5;
                     transform: scale(0.8);
                  }
               }
               @keyframes bounce {
                  0%, 80%, 100% {
                     transform: scale(0);
                     opacity: 0.5;
                  }
                  40% {
                     transform: scale(1);
                     opacity: 1;
                  }
               }
            `}</style>
         </div>
      </Modal>
   );
}

